import { useUser } from '@folklore/auth';
import { useTransition, animated } from '@react-spring/web';
import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useAnswerQuestion } from '../../hooks/useGame';
import useTimer from '../../hooks/useTimer';

import { useContest } from '../../contexts/ContestContext';
import { useThemeClassName } from '../../contexts/ThemeContext';
import GameBanner from '../partials/GameBanner';
import UserBanner from '../partials/UserBanner';
import Step from '../steps/Step';

import styles from '../../../styles/games/live-quiz.module.scss';

interface LiveQuizGameProps {
    game: LiveQuizGame;
    participation?: Participation | null;
    canPlay: boolean;
    className: string | null;
}

function LiveQuizGame({ game, participation, canPlay, className }: LiveQuizGameProps) {
    const themeClassName = useThemeClassName(styles);
    const user = useUser();
    const { handle } = useContest();
    const { points: userPoints } = user || {};

    const {
        questions = null,
        step = null,
        steps,
        leaderboard,
        requiresPincode = false,
    } = game || {};
    const { participations = null } = leaderboard || {};

    // console.log('game leaderboard', leaderboard);

    const stepData = (steps || []).find((s) => s.id === step) || null;

    const [currentView, setCurrentView] = useState(step);
    useEffect(() => setCurrentView(step), [step, setCurrentView]);

    const pages = useMemo(() => {
        const nextCurrentView = step;
        return [
            currentView,
            nextCurrentView !== currentView ? nextCurrentView : null,
            nextCurrentView === null ? 'default' : null,
        ].filter((it) => it !== null);
    }, [step, currentView]);

    const transitions = useTransition(pages, {
        initial: { opacity: 1 },
        from: { opacity: 0 },
        enter: { opacity: 1 },
        leave: { opacity: 0 },
    });

    const [prefix = null, questionId = null] = (step || '').split('-');
    const isQuestion = prefix === 'question';
    const isLeaderboard = prefix === 'leaderboard';
    const currentQuestion = isQuestion ? questions.find((q) => q.id === questionId) || null : null;

    const {
        answers = null,
        points = null,
        pincode: participationPincode = null,
    } = participation || {};

    const currentAnswer = isQuestion
        ? (answers || []).find((a) => a.question === questionId) || null
        : null;

    const { answerQuestion, isMutating: loading } = useAnswerQuestion({
        game,
        participation,
    });

    const onAnswer = useCallback(
        (answerId) => {
            if (!loading && answerId !== null) {
                // console.log('come on baby', answerId);
                answerQuestion({
                    questionId,
                    answer: answerId,
                });
            }
        },
        [answerQuestion, questionId, loading],
    );

    // TODO: IMPORTANT
    // enable this for blocking live concours with pins
    // console.log('pincode, lastCode', pincode, lastCode);
    const locked =
        requiresPincode &&
        participationPincode === null &&
        ['pincode', 'wait', 'start', 'end', 'games'].indexOf(step) === -1;

    const { endAt = null, expired = false } = currentQuestion || {};
    const [showAnswer, setShowAnswer] = useState(expired && endAt !== null);
    const onEnd = useCallback(() => {
        setShowAnswer(true);
    }, [setShowAnswer]);
    const timerParams = useMemo(() => ({ endAt, onEnd }), [endAt, onEnd]);
    const { ended, onEnded } = useTimer(timerParams);

    // If end at clears early, show the answer
    useEffect(() => {
        if (expired && endAt !== null) {
            setShowAnswer(true);
        }
    }, [expired, endAt, setShowAnswer]);

    // Assumes wake?
    const onFocus = useCallback(() => {
        if (typeof window !== 'undefined') {
            window.location.reload();
        }
    }, []);
    useEffect(() => {
        if (typeof window !== 'undefined') {
            window.addEventListener('online', onFocus);
        }
        return () => {
            if (typeof window !== 'undefined') {
                window.removeEventListener('online', onFocus);
            }
        };
    }, [onFocus]);

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                    [themeClassName]: themeClassName !== null,
                },
            ])}
        >
            {transitions((style, item) => {
                const isLeaving = item !== currentView;
                const component = locked ? 'locked' : step;
                const finalComponent =
                    !locked && (isLeaderboard || isQuestion) ? prefix : component;
                return (
                    <animated.div
                        style={style}
                        className={classNames([
                            styles.animatedContainer,
                            { [styles.isLeaving]: isLeaving, [styles[step]]: step !== null },
                        ])}
                    >
                        <GameBanner
                            game={game}
                            participations={participations}
                            count={
                                questionId !== null
                                    ? `Question ${questionId} / ${(questions || []).length}`
                                    : null
                            }
                            endAt={endAt}
                            ended={ended}
                            onEnded={onEnded}
                        />
                        {!canPlay ? (
                            <p className={styles.ended}>
                                <FormattedMessage
                                    defaultMessage="Game ended"
                                    description="Game label"
                                />
                            </p>
                        ) : (
                            <Step
                                className={styles.content}
                                innerClassName={styles.inner}
                                component={finalComponent}
                                game={game}
                                participation={participation}
                                leaderboard={leaderboard}
                                step={stepData}
                                question={currentQuestion}
                                isEnded={ended}
                                answer={currentAnswer}
                                showAnswer={showAnswer}
                                onAnswer={onAnswer}
                                isUser
                            />
                        )}
                    </animated.div>
                );
            })}
            <UserBanner
                className={styles.banner}
                user={user}
                points={handle === 'molson_2025_conference' ? userPoints : points}
            />
        </div>
    );
}

export default LiveQuizGame;
